//#region types
import { SalaryDeviationFormTypes } from "models/formTypes";
import { MonthInputProps, WintFormConnect } from "models/propTypes/wintFormPropsTypes";
import { AmountSectionProps } from "models/propTypes/wintSalaryPropTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import React from "react";
import {
  AdvancedEditableTable,
  EditableTable,
  FormFieldLayout,
  NumberInput,
  TextInput,
  VerticalFormField,
} from "components/common/wintForms";
import MonthInput from "components/common/wintForms/formFields/monthInput";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import SalaryDeviationHelper from "./salaryDeviationHelper";

const AmountSection: React.FC<AmountSectionProps> = (props) => {
  const { connect, values, labelText, readOnly } = props;

  const sumDescriptionsConnector = connect("amount.sumDescriptions");
  const isHours = SalaryDeviationHelper.isHoursDeviation(values.deviation);
  // Commission is one deal per deviation: a single fixed row (no add/remove), and the month the
  // engine resolved stays put — moving a deal to another month risks paying it twice.
  const isCommission = SalaryDeviationHelper.isCommission(values.deviation);

  const descriptionField = {
    Component: TextInput,
    extraProps: {
      label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.Description),
      disabled: readOnly,
    },
  };

  return (
    <>
      <VerticalFormField<MonthInputProps>
        Component={MonthInput}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.Month)}
        {...connect("amount.yearMonth")}
        descriptionText={
          isCommission
            ? undefined
            : LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.SalaryDeviation.CompensationMonthDescription
              )
        }
        showMainLabel
        disabled={readOnly || isCommission}
      />
      <FormFieldLayout
        {...sumDescriptionsConnector}
        direction="vertical"
        label={LocalizationHelperInstance.getFormattedMessage(
          isCommission
            ? TranslationKeys.SalaryDeviation.CommissionAmountLabel
            : TranslationKeys.SalaryDeviation.AddCompensationLabel
        )}
        descriptionText={
          isCommission
            ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.CommissionDescriptionHint)
            : undefined
        }
      >
        {isHours ? (
          <AdvancedEditableTable<SalaryDeviationFormTypes.SumDescription>
            connect={connect as WintFormConnect<any>}
            name={"amount.sumDescriptions"}
            value={values.amount?.sumDescriptions ?? []}
            fields={{
              hours: {
                Component: NumberInput,
                extraProps: {
                  label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.HoursPascal),
                  suffix: "h",
                  disabled: readOnly,
                },
              },
              minutes: {
                Component: NumberInput,
                extraProps: {
                  label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.MinutesPascal),
                  suffix: "min",
                  disabled: readOnly,
                  maxNumber: 59,
                },
              },
              description: descriptionField,
            }}
            idField="id"
            defaultRow={{ hours: undefined, minutes: undefined, description: undefined }}
            hideActions={readOnly}
          />
        ) : (
          <EditableTable<SalaryDeviationFormTypes.SumDescription>
            connect={connect as WintFormConnect<any>}
            name={"amount.sumDescriptions"}
            value={values.amount?.sumDescriptions ?? []}
            fields={{
              sum: {
                Component: NumberInput,
                extraProps: {
                  label: labelText,
                  disabled: readOnly,
                  decimalPoints: 2,
                },
                widthColumnCount: 4,
              },
              description: descriptionField,
            }}
            idField="id"
            defaultRow={{ sum: undefined, description: undefined }}
            hideActions={readOnly || isCommission}
            hideActionsCell={isCommission}
          />
        )}
      </FormFieldLayout>
    </>
  );
};

export default AmountSection;
